import React from 'react';

export type ProgressStepId = 1 | 2 | 3 | 4 | 5;

interface SelfServiceProgressBarProps {
  currentStep: ProgressStepId;
  completedSteps: number[];
  visitedSteps: number[];
  onStepClick: (step: ProgressStepId) => void;
  accentColor?: string;
  // CTV-471: drop the Verify pill (step 4) for signed-in users and renumber the
  // "Step X of N" label by visible position so Review reads "Step 4 of 4".
  skipVerify?: boolean;
  // True when the Continue button on the current step is disabled. When set,
  // pills strictly to the right of currentStep go non-clickable so the buyer
  // can't bypass creative-rejection / coverage / feasibility / email-verify
  // gates by jumping ahead via the rail. Back-navigation stays open.
  forwardNavDisabled?: boolean;
}

interface Step {
  id: ProgressStepId;
  label: string;
}

const steps: Step[] = [
  { id: 1, label: 'Business' },
  { id: 2, label: 'Campaign' },
  { id: 3, label: 'Commercial' },
  { id: 4, label: 'Verify' },
  { id: 5, label: 'Review & Pay' },
];

function tintFromAccent(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return `rgba(59,130,246,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const SelfServiceProgressBar: React.FC<SelfServiceProgressBarProps> = ({
  currentStep,
  completedSteps,
  visitedSteps,
  onStepClick,
  accentColor = '#2563eb',
  skipVerify = false,
  forwardNavDisabled = false,
}) => {
  // Keep the Verify pill visible while the user is actually on it — a brand-new
  // buyer is signed in (skipVerify true) the instant they verify, but stays on
  // step 4 until they click Continue. Hiding the step they're standing on would
  // render "Step 0 of N".
  const hideVerify = skipVerify && currentStep !== 4;
  const visibleSteps = hideVerify ? steps.filter((s) => s.id !== 4) : steps;
  const currentPosition = visibleSteps.findIndex((s) => s.id === currentStep) + 1;
  return (
    <div className="w-full pb-5 pt-2">
      <div className="mb-3 px-1">
        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
          Step {currentPosition} of {visibleSteps.length}
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {visibleSteps.map((step) => {
          // Pill state is positional, not state-based. `isCurrent` always wins
          // so the gradient stays visible when the user goes back to a step
          // they previously completed. Past-and-touched steps render solid
          // (the traveled trail); future-and-touched steps render as the
          // light tint (touched but ahead of the user — e.g. a draft / rerun
          // hydration prefilled them, or the user jumped forward then back).
          // Without the positional split, a 1→2→3→4 → Back → Back trace
          // leaves step 3 marked completed and step 4 marked only visited,
          // so they render with different colors even though both sit ahead
          // of the user — the inconsistency users see as "the highlighting
          // is wrong on step 3".
          const isCurrent = step.id === currentStep;
          const isCompleted = completedSteps.includes(step.id);
          const isVisited = visitedSteps.includes(step.id);
          const isTouched = isCompleted || isVisited;
          const isBefore = step.id < currentStep;
          const isAfter = step.id > currentStep;
          // Forward-nav gate: when the Continue button is disabled on the
          // current step, the buyer can't jump to any pill ahead of where
          // they are — back-nav is still allowed so they can fix earlier
          // input. Without this the top bar bypasses the Continue gates
          // (creative rejections, coverage, over-cap, email verify,
          // feasibility) and the buyer can land on Review with broken state.
          const isClickable = isCurrent || (isTouched && !(isAfter && forwardNavDisabled));

          let background: string;
          let pillState: 'current' | 'completed' | 'visited' | 'untouched';
          if (isCurrent) {
            background = `linear-gradient(90deg, ${accentColor}, ${tintFromAccent(accentColor, 0.35)})`;
            pillState = 'current';
          } else if (isBefore && isTouched) {
            background = accentColor;
            pillState = 'completed';
          } else if (isAfter && isTouched) {
            // CTV-558 dropped this tint to keep buyers from reading visited
            // future as "step complete." Reinstated here because, after the
            // positional split, future-touched is now visually distinct from
            // past-touched (light tint vs solid accent), so the "complete"
            // misread is resolved by the gradient/solid/tint hierarchy, not
            // by erasing the visited tint.
            background = tintFromAccent(accentColor, 0.35);
            pillState = 'visited';
          } else {
            background = 'rgb(226, 232, 240)';
            pillState = 'untouched';
          }

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => isClickable && onStepClick(step.id)}
              disabled={!isClickable}
              aria-label={`Step ${step.id}: ${step.label}`}
              data-step-state={pillState}
              className={`
                group relative flex-1 flex items-center py-3 -my-3
                ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}
              `}
            >
              <span
                className="relative w-full h-[4px] rounded-full overflow-hidden transition-transform duration-300 group-hover:scale-y-[1.6]"
                style={{ background }}
              >
                {isCurrent && (
                  <span
                    aria-hidden
                    className="absolute inset-0 animate-pulse"
                    style={{
                      background: `linear-gradient(90deg, transparent, ${tintFromAccent(accentColor, 0.6)}, transparent)`,
                    }}
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SelfServiceProgressBar;
